import api, { handleResponse, handleError } from './api';

/** A full ProBusinessDto shaped like a /pro/mine row. /pro/me has no role field: it resolves the owner's page first, so 'owner' is the honest default. */
export const toPageListItem = (d) => ({
  id: d.id, slug: d.slug, name: d.name || '', kind: d.kind || 'bookable', logoUrl: d.logoUrl || null,
  isPublished: !!d.isPublished, isListed: !!d.isListed, role: d.role || 'owner', createdAt: d.createdAt || d.memberSince || null,
});

/**
 * Pro Portal (6a) — bookable "business with faces" pages.
 */
/** Pin an owner-scoped call to one page (the cover editor on /pro/:slug edits that page, whatever persona is active). */
const pageHeaders = (pageId) => (pageId ? { headers: { 'X-Pro-Page': pageId } } : {});

const proService = {
  async suggestClubs(pageId, q = '') {
    try { return handleResponse(await api.get(`/pro/pages/${pageId}/clubs`, { params: { q } })); }
    catch (e) { return handleError(e); }
  },
  async setClub(pageId, clubId) {
    try { return handleResponse(await api.put(`/pro/pages/${pageId}/club`, { clubId })); }
    catch (e) { return handleError(e); }
  },
  async getCrafts() {
    try { return handleResponse(await api.get('/pro/crafts')); }
    catch (e) { return handleError(e); }
  },
  /** Grouped crafts with labels: { groups: [{ key, label, crafts: [{ slug, label }] }], all: [...] } */
  async getCraftGroups() {
    try { return handleResponse(await api.get('/pro/crafts/groups')); }
    catch (e) { return handleError(e); }
  },
  // ── Listed Pros (unclaimed pages) ──────────────────────────────────────
  /** REQUEST TO BOOK on a listed page. Logged out: include { email, name } and a guest session comes back as data.auth. */
  async requestBooking(slug, payload) {
    try { return handleResponse(await api.post(`/pro/${slug}/request-booking`, payload)); }
    catch (e) { return handleError(e); }
  },
  async startClaim(slug) {
    try { return handleResponse(await api.post(`/pro/${slug}/claim/start`)); }
    catch (e) { return handleError(e); }
  },
  async claimPreview(token) {
    try { return handleResponse(await api.get(`/pro/claim/${token}`)); }
    catch (e) { return handleError(e); }
  },
  async claim(token) {
    try { return handleResponse(await api.post(`/pro/claim/${token}`)); }
    catch (e) { return handleError(e); }
  },
  async requestRemoval(slug, payload) {
    try { return handleResponse(await api.post(`/pro/${slug}/request-removal`, payload)); }
    catch (e) { return handleError(e); }
  },
  /** Public directory: { items, total, skip, take }. Params: craft, q, skip, take, either regionId or lat+lng(+radius miles, default 50),
   *  and places / shops (vendor kinds are hidden unless asked for). */
  async getDirectory(params = {}) {
    try { return handleResponse(await api.get('/pro/directory', { params })); }
    catch (e) { return handleError(e); }
  },
  /** Pages per craft group under the same point / radius / kind / text filters: { groups: { key: n }, total }. */
  async getDirectoryCounts(params = {}) {
    try { return handleResponse(await api.get('/pro/directory/counts', { params })); }
    catch (e) { return handleError(e); }
  },
  // ── Close / bring back (soft delete, 30-day window) ──
  async closePage(id) {
    try { return handleResponse(await api.delete(`/pro/pages/${id}`)); } catch (e) { return handleError(e); }
  },
  async restorePage(id) {
    try { return handleResponse(await api.post(`/pro/pages/${id}/restore`)); } catch (e) { return handleError(e); }
  },
  /** The caller's closed pages still inside the window: [{ id, slug, name, kind, logoUrl, deletedAt, restoreUntil, canRestore }]. */
  async listDeleted() {
    try { return handleResponse(await api.get('/pro/deleted/mine')); } catch (e) { return handleError(e); }
  },
  /** A closed page's old URL. Public. */
  async getClosed(slug) {
    try { return handleResponse(await api.get(`/pro/closed/${encodeURIComponent(slug)}`)); } catch (e) { return handleError(e); }
  },
  /** The page the caller is acting as (X-Pro-Page), else their first one. */
  async getMine() {
    try { return handleResponse(await api.get('/pro/me')); }
    catch (e) { return handleError(e); }
  },
  /**
   * Every page the caller owns or is a member of, owned first, then by createdAt:
   * [{ id, slug, name, kind, logoUrl, isPublished, isListed, role: 'owner'|'admin'|'member', createdAt }].
   * Until the endpoint lands (404), the single /pro/me page stands in as a one-row list.
   */
  async listMine() {
    try { return handleResponse(await api.get('/pro/mine')); }
    catch (e) {
      if (e?.response?.status !== 404) return handleError(e);
      const one = await proService.getMine();
      if (one.success) return { success: true, data: one.data?.slug ? [toPageListItem(one.data)] : [], error: null };
      return one.isNetwork ? one : { success: true, data: [], error: null };
    }
  },
  /** "See a live one" on /pro: one random, fully built, published Pro page ({ slug, name, … }) or null. Public. */
  async getShowcase() {
    try { return handleResponse(await api.get('/pro/showcase')); }
    catch (e) { return handleError(e); }
  },
  async getBySlug(slug) {
    try { return handleResponse(await api.get(`/pro/${slug}`)); }
    catch (e) { return handleError(e); }
  },
  async save(data) {
    try { return handleResponse(await api.post('/pro', data)); }
    catch (e) { return handleError(e); }
  },
  /** Upload a Pro asset (gallery photo / team face / charity doc). Returns { path, url }. */
  // Availability by date. Separate from save() on purpose: the page save rebuilds
  // its child rows wholesale, which would wipe a synced calendar.
  async setAvailabilityDate(date, am, pm, note = null) {
    try {
      return handleResponse(await api.put('/pro/me/availability/date', { date, am, pm, note }));
    } catch (e) { return handleError(e); }
  },

  async clearAvailabilityDate(date) {
    try {
      return handleResponse(await api.delete(`/pro/me/availability/date/${date}`));
    } catch (e) { return handleError(e); }
  },

  // The whole hours picture at once: the normal week plus the dates that differ.
  async setAvailabilityHours(payload) {
    try { return handleResponse(await api.put('/pro/me/availability/hours', payload)); }
    catch (e) { return handleError(e); }
  },

  // What a client can actually book, day by day.
  async slots(slug, from = null, days = 30, serviceId = null) {
    try {
      const qs = new URLSearchParams({ days: String(days) });
      if (from) qs.set('from', from);
      // The length being booked comes from the service, not the page default —
      // a lesson and a wedding aren't the same job.
      if (serviceId) qs.set('serviceId', serviceId);
      return handleResponse(await api.get(`/pro/${slug}/slots?${qs}`));
    } catch (e) { return handleError(e); }
  },

  async setDateHours(date, closed, windows) {
    try { return handleResponse(await api.put('/pro/me/availability/date-hours', { date, closed, windows })); }
    catch (e) { return handleError(e); }
  },

  async clearDateHours(date) {
    try { return handleResponse(await api.delete(`/pro/me/availability/date-hours/${date}`)); }
    catch (e) { return handleError(e); }
  },

  async busyBlocks(from = null, to = null) {
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      return handleResponse(await api.get(`/pro/me/busy${qs.toString() ? `?${qs}` : ''}`));
    } catch (e) { return handleError(e); }
  },

  // Connected calendars. The feed URL is a secret (a Google iCal address grants
  // read access to that whole calendar), so it goes up and never comes back.
  async listCalendarFeeds() {
    try { return handleResponse(await api.get('/pro/me/calendar-feeds')); }
    catch (e) { return handleError(e); }
  },

  async addCalendarFeed(url, label, tzOffsetMinutes, allDayBlocksBoth = true) {
    try {
      return handleResponse(await api.post('/pro/me/calendar-feeds', {
        url, label, tzOffsetMinutes, allDayBlocksBoth,
      }));
    } catch (e) { return handleError(e); }
  },

  async syncCalendarFeed(feedId) {
    try { return handleResponse(await api.post(`/pro/me/calendar-feeds/${feedId}/sync`)); }
    catch (e) { return handleError(e); }
  },

  async removeCalendarFeed(feedId) {
    try { return handleResponse(await api.delete(`/pro/me/calendar-feeds/${feedId}`)); }
    catch (e) { return handleError(e); }
  },

  async bookingFeedUrl() {
    try { return handleResponse(await api.post('/pro/me/booking-feed')); }
    catch (e) { return handleError(e); }
  },

  async uploadAsset(file) {
    try {
      const form = new FormData();
      form.append('file', file);
      return handleResponse(await api.post('/pro/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } }));
    } catch (e) { return handleError(e); }
  },

  /** EDIT COVER on the Pro page: layout · split · photoCount · order · focal · cardId · addPaths · remove. */
  async updateCover(pageId, data) {
    try { return handleResponse(await api.put(`/pro/pages/${pageId}/cover`, data)); }
    catch (e) { return handleError(e); }
  },

  /** DM this Page — returns the thread summary ({ id, ... }). */
  async messagePage(slug) {
    try { return handleResponse(await api.post(`/pro/${slug}/message`)); }
    catch (e) { return handleError(e); }
  },

  // ---- Page roles / staff ----
  async listMembers(slug) {
    try { return handleResponse(await api.get(`/pro/${slug}/members`)); }
    catch (e) { return handleError(e); }
  },
  async addMember(slug, userId, role) {
    try { return handleResponse(await api.post(`/pro/${slug}/members`, { userId, role })); }
    catch (e) { return handleError(e); }
  },
  async updateMemberRole(slug, userId, role) {
    try { return handleResponse(await api.patch(`/pro/${slug}/members/${userId}`, { role })); }
    catch (e) { return handleError(e); }
  },
  async removeMember(slug, userId) {
    try { return handleResponse(await api.delete(`/pro/${slug}/members/${userId}`)); }
    catch (e) { return handleError(e); }
  },
  async transferOwnership(slug, userId) {
    try { return handleResponse(await api.post(`/pro/${slug}/transfer`, { userId })); }
    catch (e) { return handleError(e); }
  },
  /** The TRANSFER OWNERSHIP card: viewer role, the managers it can go to, the open request if any. Owner / admin only. */
  async transferState(slug) {
    try { return handleResponse(await api.get(`/pro/${slug}/transfer`)); }
    catch (e) { return handleError(e); }
  },
  /** An admin asks the owner to move the page to `toUserId` (themselves or another manager). */
  async requestTransfer(slug, toUserId, reason) {
    try { return handleResponse(await api.post(`/pro/${slug}/transfer-requests`, { toUserId, reason })); }
    catch (e) { return handleError(e); }
  },
  async approveTransfer(slug, requestId) {
    try { return handleResponse(await api.post(`/pro/${slug}/transfer-requests/${requestId}/approve`)); }
    catch (e) { return handleError(e); }
  },
  async declineTransfer(slug, requestId) {
    try { return handleResponse(await api.post(`/pro/${slug}/transfer-requests/${requestId}/decline`)); }
    catch (e) { return handleError(e); }
  },
  async withdrawTransfer(slug, requestId) {
    try { return handleResponse(await api.post(`/pro/${slug}/transfer-requests/${requestId}/withdraw`)); }
    catch (e) { return handleError(e); }
  },

  // ---- Booking negotiation (6b) ----

  /** Make an offer to a Pro (signed-in). Body: { amountCents, email, name?, eventDate?, message? } */
  async makeOffer(slug, data) {
    try { return handleResponse(await api.post(`/pro/${slug}/offers`, data)); }
    catch (e) { return handleError(e); }
  },
  /** Accountless make-an-offer. Returns { offer, auth } — auth is a stub session to log in with. */
  async makeGuestOffer(slug, data) {
    try { return handleResponse(await api.post(`/pro/${slug}/guest-offers`, data)); }
    catch (e) { return handleError(e); }
  },
  /** Magic link: exchange an offer's access token for a guest session (returns an auth payload
   * to applyGuestSession). Lets a returning guest open + pay for their offer from any device. */
  async guestOfferSession(offerId, token) {
    try { return handleResponse(await api.get(`/pro/offers/${offerId}/guest-session`, { params: { token } })); }
    catch (e) { return handleError(e); }
  },
  /** Earnings + payout readiness for the Desk (6e). */
  async getEarnings() {
    try { return handleResponse(await api.get('/pro/earnings')); }
    catch (e) { return handleError(e); }
  },
  /** Pro subscription status (6e). */
  async getSubscription() {
    try { return handleResponse(await api.get('/pro/subscription')); }
    catch (e) { return handleError(e); }
  },
  /** Start the monthly Pro subscription. Body: { stripePaymentMethodId, stripeCustomerId? } */
  async startSubscription(data) {
    try { return handleResponse(await api.post('/pro/subscription', data)); }
    catch (e) { return handleError(e); }
  },
  async cancelSubscription() {
    try { return handleResponse(await api.post('/pro/subscription/cancel')); }
    catch (e) { return handleError(e); }
  },
  /** Offers received across the caller's Pro pages (the Desk). */
  async incomingOffers() {
    try { return handleResponse(await api.get('/pro/offers/incoming')); }
    catch (e) { return handleError(e); }
  },
  /** Offers the caller sent as a buyer. */
  // ── Cover video ──────────────────────────────────────────────────────────
  // Optional motion behind the Pro header. Either a file we host on Cloudflare
  // Stream, or a YouTube/Vimeo link.

  /** One-time Cloudflare upload URL; the file goes straight there, not via our API. */
  async startCoverVideoUpload(pageId = null) {
    try { return handleResponse(await api.post('/pro/me/cover-video/upload-url', null, pageHeaders(pageId))); }
    catch (e) { return handleError(e); }
  },

  /** POST the file to the Cloudflare URL. Outside our API, so plain fetch. */
  async uploadCoverVideoFile(uploadUrl, file) {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(uploadUrl, { method: 'POST', body: fd });
      return res.ok ? { success: true } : { success: false, error: 'Upload failed.' };
    } catch (e) {
      return { success: false, error: e?.message || 'Upload failed.' };
    }
  },

  /** Point the cover at a YouTube or Vimeo link. */
  async setCoverVideoEmbed(url, pageId = null) {
    try { return handleResponse(await api.put('/pro/me/cover-video/embed', { url }, pageHeaders(pageId))); }
    catch (e) { return handleError(e); }
  },

  /** Poll while an upload encodes — watch `ready`. */
  async getCoverVideo(pageId = null) {
    try { return handleResponse(await api.get('/pro/me/cover-video', pageHeaders(pageId))); }
    catch (e) { return handleError(e); }
  },

  async clearCoverVideo(pageId = null) {
    try { return handleResponse(await api.delete('/pro/me/cover-video', pageHeaders(pageId))); }
    catch (e) { return handleError(e); }
  },

  async myOffers() {
    try { return handleResponse(await api.get('/pro/offers/mine')); }
    catch (e) { return handleError(e); }
  },
  async getOffer(offerId) {
    try { return handleResponse(await api.get(`/pro/offers/${offerId}`)); }
    catch (e) { return handleError(e); }
  },
  async counterOffer(offerId, data) {
    try { return handleResponse(await api.post(`/pro/offers/${offerId}/counter`, data)); }
    catch (e) { return handleError(e); }
  },
  async acceptOffer(offerId) {
    try { return handleResponse(await api.post(`/pro/offers/${offerId}/accept`)); }
    catch (e) { return handleError(e); }
  },
  async declineOffer(offerId) {
    try { return handleResponse(await api.post(`/pro/offers/${offerId}/decline`)); }
    catch (e) { return handleError(e); }
  },
  /** Cancel an offer or a paid booking. Body: { reason? }. A paid one refunds the card (see offer.cancelQuote). */
  async cancelOffer(offerId, data = {}) {
    try { return handleResponse(await api.post(`/pro/offers/${offerId}/cancel`, data)); }
    catch (e) { return handleError(e); }
  },
  /** Propose new times. Body: { starts: [iso, …] (one per session still to come), note? } */
  async proposeReschedule(offerId, data) {
    try { return handleResponse(await api.post(`/pro/offers/${offerId}/reschedule`, data)); }
    catch (e) { return handleError(e); }
  },
  /** answer: 'accept' | 'decline' | 'withdraw' */
  async answerReschedule(offerId, rescheduleId, answer) {
    try { return handleResponse(await api.post(`/pro/offers/${offerId}/reschedule/${rescheduleId}/${answer}`)); }
    catch (e) { return handleError(e); }
  },
  /** Pay + escrow. Body: { stripePaymentMethodId, stripeCustomerId?, email, name? } */
  async proceedOffer(offerId, data) {
    try { return handleResponse(await api.post(`/pro/offers/${offerId}/proceed`, data)); }
    catch (e) { return handleError(e); }
  },
  /** Leave an escrow-verified testimonial on a released booking. Body: { rating, text? } */
  async leaveTestimonial(bookingId, data) {
    try { return handleResponse(await api.post(`/pro/bookings/${bookingId}/testimonial`, data)); }
    catch (e) { return handleError(e); }
  },

  // ---- Disputes (6d) ----

  /** Flag a confirmed booking. Body: { reason, freeText?, desiredOutcome? } */
  async flagBooking(bookingId, data) {
    try { return handleResponse(await api.post(`/pro/bookings/${bookingId}/flag`, data)); }
    catch (e) { return handleError(e); }
  },
  /** Admin: open dispute review queue. */
  async openFlags() {
    try { return handleResponse(await api.get('/pro/flags/open')); }
    catch (e) { return handleError(e); }
  },
  /** Admin: resolve. Body: { resolution, splitToBuyerCents?, note?, strikeSide? } */
  async resolveFlag(flagId, data) {
    try { return handleResponse(await api.post(`/pro/flags/${flagId}/resolve`, data)); }
    catch (e) { return handleError(e); }
  },
  /** Flagged party responds. Body: { action: 'make_right'|'contest', offerToBuyerCents?, note? } */
  async respondFlag(flagId, data) {
    try { return handleResponse(await api.post(`/pro/flags/${flagId}/respond`, data)); }
    catch (e) { return handleError(e); }
  },
  /** Filer withdraws their flag. */
  async withdrawFlag(flagId) {
    try { return handleResponse(await api.post(`/pro/flags/${flagId}/withdraw`)); }
    catch (e) { return handleError(e); }
  },
};

export default proService;
